import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import ConfirmModal from './ConfirmModal';
import LoadingSpinner from './LoadingSpinner';

interface Supplier {
  id: number;
  name: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  address?: string;
}

const Suppliers: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState<Supplier[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState<number | null>(null);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredSuppliers(suppliers);
    } else {
      const filtered = suppliers.filter(supplier =>
        supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSuppliers(filtered);
    }
  }, [searchTerm, suppliers]);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/suppliers/');
      setSuppliers(response.data);
      setFilteredSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await api.put(`/suppliers/${editingSupplier.id}`, formData);
      } else {
        await api.post('/suppliers/', formData);
      }
      setShowModal(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      console.error('Error saving supplier:', error);
      alert('Erreur lors de l\'enregistrement du fournisseur');
    }
  };

  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      contact_person: supplier.contact_person || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    setSupplierToDelete(id);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!supplierToDelete) return;
    
    try {
      await api.delete(`/suppliers/${supplierToDelete}`);
      fetchSuppliers();
    } catch (error) {
      console.error('Error deleting supplier:', error);
      alert('Erreur lors de la suppression du fournisseur');
    }
  };

  const resetForm = () => {
    setEditingSupplier(null);
    setFormData({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: ''
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (loading) {
    return <LoadingSpinner message="Chargement des fournisseurs..." />;
  }

  return (
    <div className="suppliers-container">
      <div className="page-header">
        <h2>🏢 Gestion des Fournisseurs</h2>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          ➕ Nouveau Fournisseur
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="🔍 Rechercher par nom, contact ou email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredSuppliers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏢</div>
          <p className="empty-message">
            {searchTerm ? 'Aucun fournisseur ne correspond à votre recherche' : 'Aucun fournisseur disponible'}
          </p>
          {!searchTerm && (
            <button className="btn-primary" onClick={() => setShowModal(true)}>
              Créer votre premier fournisseur
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop: Table View */}
          <div className="suppliers-table-view">
            <div className="table-container">
              <table className="stock-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Adresse</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.map(supplier => (
                    <tr key={supplier.id}>
                      <td><strong>{supplier.name}</strong></td>
                      <td>{supplier.contact_person || '-'}</td>
                      <td>{supplier.email || '-'}</td>
                      <td>{supplier.phone || '-'}</td>
                      <td className="address-cell">{supplier.address || '-'}</td>
                      <td>
                        <div className="actions-cell">
                          <button 
                            className="btn-icon btn-edit" 
                            onClick={() => handleEdit(supplier)}
                            title="Modifier"
                          >
                            ✏️
                          </button>
                          <button 
                            className="btn-icon btn-delete" 
                            onClick={() => handleDelete(supplier.id)}
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile: Cards View */}
          <div className="suppliers-cards-view">
            {filteredSuppliers.map(supplier => (
              <div key={supplier.id} className="supplier-card">
                <div className="supplier-card-header">
                  <h3 className="supplier-name">{supplier.name}</h3>
                </div>
                
                <div className="supplier-card-body">
                  {supplier.contact_person && (
                    <div className="supplier-info-item">
                      <span className="info-label">👤 Contact</span>
                      <span className="info-value">{supplier.contact_person}</span>
                    </div>
                  )}
                  {supplier.email && (
                    <div className="supplier-info-item">
                      <span className="info-label">📧 Email</span>
                      <a href={`mailto:${supplier.email}`} className="info-value link">{supplier.email}</a>
                    </div>
                  )}
                  {supplier.phone && (
                    <div className="supplier-info-item">
                      <span className="info-label">📞 Téléphone</span>
                      <a href={`tel:${supplier.phone}`} className="info-value link">{supplier.phone}</a>
                    </div>
                  )}
                  {supplier.address && (
                    <div className="supplier-info-item">
                      <span className="info-label">📍 Adresse</span>
                      <span className="info-value">{supplier.address}</span>
                    </div>
                  )}
                </div>
                
                <div className="supplier-card-actions">
                  <button 
                    className="btn-card btn-edit" 
                    onClick={() => handleEdit(supplier)}
                  >
                    <span className="btn-icon">✏️</span>
                    <span className="btn-text">Modifier</span>
                  </button>
                  <button 
                    className="btn-card btn-delete" 
                    onClick={() => handleDelete(supplier.id)}
                  >
                    <span className="btn-icon">🗑️</span>
                    <span className="btn-text">Supprimer</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal} role="dialog" aria-modal="true">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingSupplier ? 'Modifier le Fournisseur' : 'Nouveau Fournisseur'}</h3>
              <button className="btn-close" onClick={handleCloseModal} aria-label="Fermer">✕</button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Nom du Fournisseur *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Ex: Distributeur ABC"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact_person">Personne de Contact</label>
                <input
                  type="text"
                  id="contact_person"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleInputChange}
                  placeholder="Nom du contact"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Téléphone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+33 1 23 45 67 89"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address">Adresse Complète</label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Adresse complète du fournisseur"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingSupplier ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showConfirm}
        title="Supprimer le fournisseur"
        message="Êtes-vous sûr de vouloir supprimer ce fournisseur ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirm(false);
          setSupplierToDelete(null);
        }}
      />
    </div>
  );
};

export default Suppliers;
